import { Component, type ReactNode, type ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import { isChunkLoadError, handleChunkError } from '@/utils/chunkRetry';
import { ErrorFallback } from '@/components/ErrorFallback';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error) && handleChunkError()) return;
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  handleReload = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100dvh',
            padding: '2rem',
            backgroundColor: 'var(--bg, #f8efdf)',
          }}
        >
          <ErrorFallback
            title="Something went wrong"
            message="An unexpected error occurred. Please reload the page to continue."
            actions={[{ label: 'Reload App', onClick: this.handleReload, variant: 'primary' }]}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
